import { useDeferredValue, useReducer, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  orderingApi,
  type CatalogOffer,
  type OrderDetail,
  type UpdateOrderInput,
  type UpdateReceiptInput,
} from "./orderingApi";
import { priceToCents } from "./orderingFormUtils";

const inputClass = "rounded border border-slate-300 px-2 py-1.5 text-sm";

function useRefreshEditedOrder(onSaved: () => void) {
  const queryClient = useQueryClient();
  return async (order: OrderDetail) => {
    queryClient.setQueryData(["ordering-order", order.order.id], order);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["ordering-orders"] }),
      queryClient.invalidateQueries({
        queryKey: ["ordering-order", order.order.id],
      }),
    ]);
    onSaved();
  };
}

function EditActions({
  isSaving,
  onCancel,
}: {
  isSaving: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="flex gap-2">
      <button
        type="submit"
        disabled={isSaving}
        className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
      >
        {isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
      </button>
      <button
        type="button"
        disabled={isSaving}
        onClick={onCancel}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm disabled:opacity-50"
      >
        Annuler
      </button>
    </div>
  );
}

interface OrderEditLine {
  key: string;
  label: string;
  supplierReference: string;
  isInvoiced: boolean;
  quantity: string;
  input: UpdateOrderInput["lines"][number];
}

type OrderEditAction =
  | { type: "quantity"; key: string; quantity: string }
  | { type: "remove"; key: string }
  | { type: "add"; offer: CatalogOffer };

function orderEditReducer(lines: OrderEditLine[], action: OrderEditAction) {
  switch (action.type) {
    case "quantity":
      return lines.map((line) =>
        line.key === action.key ? { ...line, quantity: action.quantity } : line,
      );
    case "remove":
      return lines.filter((line) => line.key !== action.key || line.isInvoiced);
    case "add": {
      if (
        lines.some(
          (line) => line.supplierReference === action.offer.supplierReference,
        )
      )
        return lines;
      return [
        ...lines,
        {
          key: action.offer.offerId,
          label: [action.offer.brand, action.offer.commercialName]
            .filter(Boolean)
            .join(" · "),
          supplierReference: action.offer.supplierReference,
          isInvoiced: false,
          quantity: "1",
          input: { offerId: action.offer.offerId, quantity: 1 },
        },
      ];
    }
  }
}

export function OrderEditForm({
  order,
  onCancel,
  onSaved,
}: {
  order: OrderDetail;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [lines, dispatch] = useReducer(
    orderEditReducer,
    order.lines,
    (initial) =>
      initial.map((line) => ({
        key: line.id,
        label: [line.brand, line.commercialName].filter(Boolean).join(" · "),
        supplierReference: line.supplierReference,
        isInvoiced: line.isInvoiced ?? false,
        quantity: String(line.orderedQuantity),
        input: { orderLineId: line.id, quantity: line.orderedQuantity },
      })),
  );
  const [search, setSearch] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const query = useDeferredValue(search).trim();
  const catalogQuery = useQuery({
    queryKey: ["ordering-catalog", "order-edit", order.order.supplierId, query],
    queryFn: () => orderingApi.searchCatalog(query, order.order.supplierId),
    enabled: query.length >= 3,
  });
  const mutation = useMutation({
    mutationFn: orderingApi.updateOrder,
    onSuccess: useRefreshEditedOrder(onSaved),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidationError(null);
    if (lines.length === 0) {
      setValidationError("La commande doit contenir au moins un article.");
      return;
    }
    const updatedLines = lines.map((line) => ({
      ...line.input,
      quantity: Number(line.quantity),
    }));
    if (
      updatedLines.some(
        (line) =>
          !Number.isInteger(line.quantity) ||
          line.quantity <= 0 ||
          line.quantity > 2_147_483_647,
      )
    ) {
      setValidationError(
        "Les quantités doivent être des nombres entiers positifs.",
      );
      return;
    }
    mutation.mutate({ orderId: order.order.id, lines: updatedLines });
  };
  return (
    <form
      onSubmit={submit}
      className="space-y-4"
      aria-label="Modifier la commande"
    >
      <h3 className="font-semibold">Modifier la commande</h3>
      <p className="text-sm text-slate-500">
        Articles du revendeur {order.order.supplierName}. La date et les prix
        existants sont conservés.
      </p>
      <fieldset
        disabled={mutation.isPending}
        className="space-y-4 disabled:opacity-60"
      >
        {lines.map((line) => (
          <div
            key={line.key}
            className="flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 p-3"
          >
            <div className="min-w-40 flex-1 text-sm">
              <p className="font-medium">{line.label}</p>
              <p className="text-slate-500">{line.supplierReference}</p>
              {line.isInvoiced ? (
                <p className="text-xs text-slate-500">
                  Lié à une facture : suppression impossible.
                </p>
              ) : null}
            </div>
            <input
              className={`${inputClass} w-24`}
              type="number"
              min="1"
              max="2147483647"
              step="1"
              required
              aria-label={`Quantité commandée pour ${line.label}`}
              value={line.quantity}
              onChange={(event) =>
                dispatch({
                  type: "quantity",
                  key: line.key,
                  quantity: event.target.value,
                })
              }
            />
            <button
              type="button"
              disabled={line.isInvoiced}
              className="text-sm text-red-700 disabled:opacity-40"
              aria-label={`Retirer ${line.label}`}
              onClick={() => dispatch({ type: "remove", key: line.key })}
            >
              Retirer
            </button>
          </div>
        ))}
        <label className="block text-sm font-medium">
          Ajouter un article du même revendeur
          <input
            className={`${inputClass} mt-1 block w-full`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher dans le catalogue (3 caractères minimum)"
          />
        </label>
        {query.length >= 3 ? (
          <div className="max-h-64 space-y-2 overflow-auto">
            {catalogQuery.isLoading ? (
              <p className="text-sm text-slate-500">Recherche...</p>
            ) : null}
            {catalogQuery.error ? (
              <p role="alert" className="text-sm text-red-700">
                {catalogQuery.error.message}
              </p>
            ) : null}
            {catalogQuery.data
              ?.filter((offer) => offer.supplierId === order.order.supplierId)
              .map((offer) => {
                const present = lines.some(
                  (line) => line.supplierReference === offer.supplierReference,
                );
                return (
                  <div
                    key={offer.offerId}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span>
                      {[
                        offer.brand,
                        offer.commercialName,
                        offer.supplierReference,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <button
                      type="button"
                      disabled={present}
                      className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
                      aria-label={`Ajouter ${offer.commercialName} à la commande`}
                      onClick={() => dispatch({ type: "add", offer })}
                    >
                      {present ? "Déjà ajouté" : "Ajouter"}
                    </button>
                  </div>
                );
              })}
            {catalogQuery.data?.length === 0 ? (
              <p className="text-sm text-slate-500">
                Aucun article trouvé pour ce revendeur.
              </p>
            ) : null}
          </div>
        ) : null}
      </fieldset>
      {validationError || mutation.error ? (
        <p role="alert" className="text-sm text-red-700">
          {validationError ?? mutation.error?.message}
        </p>
      ) : null}
      <EditActions isSaving={mutation.isPending} onCancel={onCancel} />
    </form>
  );
}

export function ReceiptEditForm({
  order,
  receipt,
  onCancel,
  onSaved,
}: {
  order: OrderDetail;
  receipt: OrderDetail["receipts"][number];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: orderingApi.updateReceipt,
    onSuccess: useRefreshEditedOrder(onSaved),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidationError(null);
    const data = new FormData(event.currentTarget);
    const lines: UpdateReceiptInput["lines"] = [];
    for (const line of order.lines) {
      const receivedQuantity = Number(data.get(`quantity-${line.id}`));
      const paidUnitPriceCents = priceToCents(data.get(`price-${line.id}`));
      const previousQuantity =
        receipt.lines.find((previous) => previous.orderLineId === line.id)
          ?.receivedQuantity ?? 0;
      const maximum =
        line.orderedQuantity - line.receivedQuantity + previousQuantity;
      if (
        !Number.isInteger(receivedQuantity) ||
        receivedQuantity < 0 ||
        receivedQuantity > maximum ||
        (receivedQuantity > 0 && paidUnitPriceCents === null)
      ) {
        setValidationError("Vérifiez les quantités reçues et les prix payés.");
        return;
      }
      if (receivedQuantity > 0 && paidUnitPriceCents !== null)
        lines.push({
          orderLineId: line.id,
          receivedQuantity,
          paidUnitPriceCents,
        });
    }
    if (lines.length === 0) {
      setValidationError(
        "Conservez au moins une ligne reçue dans cette réception.",
      );
      return;
    }
    mutation.mutate({ orderId: order.order.id, receiptId: receipt.id, lines });
  };
  return (
    <form
      onSubmit={submit}
      className="space-y-3"
      aria-label="Modifier la réception"
    >
      <h4 className="font-semibold">Modifier la réception</h4>
      <p className="text-sm text-slate-500">
        Les factures importées restent inchangées. Une quantité de 0 retire
        l’article de cette réception.
      </p>
      <fieldset
        disabled={mutation.isPending}
        className="space-y-3 disabled:opacity-60"
      >
        {order.lines.map((line) => {
          const previous = receipt.lines.find(
            (item) => item.orderLineId === line.id,
          );
          const maximum =
            line.orderedQuantity -
            line.receivedQuantity +
            (previous?.receivedQuantity ?? 0);
          return (
            <div
              key={line.id}
              className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_100px_130px]"
            >
              <div className="text-sm">
                <p className="font-medium">
                  {[line.brand, line.commercialName]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <p className="text-xs text-slate-500">
                  Maximum pour cette réception : {maximum}
                </p>
              </div>
              <label className="text-xs text-slate-500">
                Quantité reçue
                <input
                  className={`${inputClass} block w-full text-slate-900`}
                  type="number"
                  min="0"
                  max={maximum}
                  step="1"
                  required
                  name={`quantity-${line.id}`}
                  defaultValue={previous?.receivedQuantity ?? 0}
                  aria-label={`Quantité corrigée pour ${line.commercialName}`}
                />
              </label>
              <label className="text-xs text-slate-500">
                Prix unitaire payé (€)
                <input
                  className={`${inputClass} block w-full text-slate-900`}
                  inputMode="decimal"
                  name={`price-${line.id}`}
                  defaultValue={(
                    (previous?.paidUnitPriceCents ??
                      line.catalogUnitPriceCents) / 100
                  ).toFixed(2)}
                  aria-label={`Prix corrigé pour ${line.commercialName}`}
                />
              </label>
            </div>
          );
        })}
      </fieldset>
      {validationError || mutation.error ? (
        <p role="alert" className="text-sm text-red-700">
          {validationError ?? mutation.error?.message}
        </p>
      ) : null}
      <EditActions isSaving={mutation.isPending} onCancel={onCancel} />
    </form>
  );
}
